/**
 * @description
 * Error type definitions for the MediConnect Healthcare Information System
 * These interfaces define the structure of error types and healthcare-specific
 * error handling throughout the application.
 */

import { ApiError, HealthcareErrorCode } from './response-types';

/**
 * Custom healthcare API error class
 */
export class HealthcareApiError extends Error {
  statusCode: number;
  errors?: Array<{ field?: string; message: string; code?: string }>;
  timestamp: string;
  path?: string;
  code?: HealthcareErrorCode | string;
  
  constructor(error: ApiError) {
    super(error.message);
    this.name = 'HealthcareApiError';
    this.statusCode = error.statusCode;
    this.errors = error.errors;
    this.timestamp = error.timestamp;
    this.path = error.path;
    this.code = error.code;
    
    // Needed for instanceof to work correctly in TypeScript
    Object.setPrototypeOf(this, HealthcareApiError.prototype);
  }
  
  /**
   * Determines if this error is related to authentication
   */
  isAuthError(): boolean {
    return this.statusCode === 401 || this.statusCode === 403;
  }
  
  /**
   * Determines if this error is related to invalid input
   */
  isValidationError(): boolean {
    return this.statusCode === 400 && Array.isArray(this.errors) && this.errors.length > 0;
  }
  
  /**
   * Determines if this error is a HIPAA-related error
   */
  isHipaaError(): boolean {
    return this.code === HealthcareErrorCode.HIPAA_VIOLATION || 
           this.code === HealthcareErrorCode.PHI_AUTHORIZATION_REQUIRED;
  }
  
  /**
   * Determines if this is a network error (not server-specific)
   */
  isNetworkError(): boolean {
    return this.statusCode === 0 || !this.statusCode;
  }
  
  /**
   * Returns the error message for display to the user
   * Sanitizes any potentially sensitive information
   */
  getUserFriendlyMessage(): string {
    // For validation errors, return a formatted list of validation issues
    if (this.isValidationError() && this.errors) {
      return this.errors
        .map(err => `${err.field ? `${err.field}: ` : ''}${err.message}`)
        .join('\n');
    }
    
    // For HIPAA-related errors, return a standard message
    if (this.isHipaaError()) {
      return 'Access to protected health information is restricted. Please verify your permissions.';
    }
    
    // For network errors, return a connection error message
    if (this.isNetworkError()) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    
    // For other errors, return the error message if appropriate for user display
    // or a generic message if the error is too technical
    return this.message || 'An unexpected error occurred. Please try again.';
  }
} 